import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, CheckCircle, Clock, Play } from 'lucide-react';
import MathRenderer from '../components/MathRenderer';

export default function StudentAssignments() {
  const { API, getHeaders } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [steps, setSteps] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/student/assignments`, { headers: getHeaders() });
      const data = await res.json();
      setAssignments(data.assignments || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const startAssignment = async (assignmentId) => {
    try {
      const res = await fetch(`${API}/student/assignment/${assignmentId}`, { headers: getHeaders() });
      const data = await res.json();
      if (data.attempt?.completed) {
        toast.info(`Already completed! Score: ${data.attempt.score_percent}%`);
        return;
      }
      setActiveAssignment(data.assignment);
      setQuestions(data.questions || []);
      setCurrentIdx(0);
      setAnswers({});
      setSteps({});
      setSubmitted(false);
      setResult(null);
    } catch { toast.error('Failed to load assignment'); }
  };

  const submitAll = async () => {
    const answerList = questions.map(q => ({
      question_id: q.id,
      answer: answers[q.id] || '',
      steps: steps[q.id] || '',
    }));
    try {
      const res = await fetch(`${API}/student/assignment/${activeAssignment.id}/submit`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ answers: answerList }),
      });
      const data = await res.json();
      setResult(data);
      setSubmitted(true);
      toast.success(`Score: ${data.score_percent}% | +${data.coins_earned} coins!`);
    } catch { toast.error('Submit failed'); }
  };

  const q = questions[currentIdx];

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FFF8F0' }}>
      <div className="space-y-3 w-full max-w-md px-4">
        {[1,2,3].map(i => <div key={i} className="h-20 bg-[#F0E6D8] rounded-2xl animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pb-6" style={{ background: '#FFF8F0' }}>
      <div className="sticky top-0 z-20 bg-white border-b-2 border-[#E5DACE] px-4 py-3 flex items-center gap-3">
        <button onClick={() => activeAssignment ? setActiveAssignment(null) : navigate('/home')} data-testid="assignments-back-btn">
          <ArrowLeft className="w-5 h-5 text-[#1B4F8A]" />
        </button>
        <ClipboardList className="w-5 h-5 text-[#FF6B35]" />
        <h1 className="font-extrabold text-[#1B4F8A] text-lg">
          {activeAssignment ? activeAssignment.title : 'My Assignments'}
        </h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 mt-4">
        {/* Assignment List */}
        {!activeAssignment && (
          <>
            {assignments.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-[#E5DACE] mx-auto mb-3" />
                <p className="font-bold text-slate-400">No assignments yet</p>
                <p className="text-sm text-slate-400">Join a class to receive assignments from your teacher</p>
              </div>
            ) : (
              <div className="space-y-2">
                {assignments.map(a => (
                  <Card key={a.id} className={`border-2 rounded-2xl cursor-pointer transition-all ${a.completed ? 'border-[#06D6A0]' : 'border-[#E5DACE] hover:border-[#FF6B35]'}`}
                    onClick={() => startAssignment(a.id)}>
                    <CardContent className="p-4 flex items-center gap-3">
                      {a.completed ? <CheckCircle className="w-6 h-6 text-[#06D6A0] shrink-0" /> : <Play className="w-6 h-6 text-[#FF6B35] shrink-0" />}
                      <div className="flex-1">
                        <p className="font-bold text-[#1B4F8A] text-sm">{a.title}</p>
                        <p className="text-xs text-slate-400">
                          {a.total_questions} questions {a.due_date && `| Due: ${a.due_date}`}
                        </p>
                      </div>
                      {a.completed && <span className="text-sm font-bold text-[#06D6A0]">{a.score}%</span>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Active Assignment - Question View */}
        {activeAssignment && !submitted && q && (
          <>
            <div className="mb-4">
              <div className="flex justify-between text-xs font-bold text-slate-500 mb-1">
                <span>Question {currentIdx + 1} of {questions.length}</span>
                <span className="capitalize text-[#FF6B35]">{q.question_type} | {q.marks}m</span>
              </div>
              <div className="h-2 bg-[#F0E6D8] rounded-full overflow-hidden">
                <div className="h-full bg-[#FF6B35] rounded-full transition-all" style={{ width: `${((currentIdx + 1) / questions.length) * 100}%` }} />
              </div>
            </div>

            <Card className="border-2 border-[#E5DACE] rounded-2xl mb-4">
              <CardContent className="p-5">
                <p className="text-xs font-bold text-slate-400 mb-2 uppercase">{q.chapter?.replace(/_/g, ' ')}</p>
                <div className="text-base font-bold text-[#1B4F8A] mb-4 leading-relaxed">
                  <MathRenderer text={q.question_latex || q.question_text} />
                </div>

                {/* MCQ / Assertion-Reason */}
                {(q.question_type === 'mcq' || q.question_type === 'assertion_reason' || !q.question_type) && (
                  <div className="space-y-2">
                    {['A','B','C','D'].map(opt => {
                      const text = q[`option_${opt.toLowerCase()}`];
                      if (!text) return null;
                      return (
                        <button key={opt} data-testid={`assign-opt-${opt}`} onClick={() => setAnswers({...answers, [q.id]: opt})}
                          className={`w-full text-left p-3 rounded-xl border-2 font-bold text-sm transition-all
                            ${answers[q.id] === opt ? 'border-[#1B4F8A] bg-[#1B4F8A]/10' : 'border-[#E5DACE]'}`}>
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F0E6D8] text-[#1B4F8A] text-xs font-extrabold mr-2">{opt}</span>
                          <MathRenderer text={text} />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Multi-select */}
                {q.question_type === 'multi_select' && (
                  <div className="space-y-2">
                    {['A','B','C','D'].map(opt => {
                      const text = q[`option_${opt.toLowerCase()}`];
                      if (!text) return null;
                      const selected = (answers[q.id] || '').split(',').filter(Boolean);
                      const isSelected = selected.includes(opt);
                      return (
                        <button key={opt} onClick={() => {
                          const newSel = isSelected ? selected.filter(s => s !== opt) : [...selected, opt];
                          setAnswers({...answers, [q.id]: newSel.join(',')});
                        }}
                          className={`w-full text-left p-3 rounded-xl border-2 font-bold text-sm transition-all
                            ${isSelected ? 'border-[#1B4F8A] bg-[#1B4F8A]/10' : 'border-[#E5DACE]'}`}>
                          <span className="mr-2">{isSelected ? '☑' : '☐'}</span>
                          <MathRenderer text={text} />
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* True/False */}
                {q.question_type === 'true_false' && (
                  <div className="flex gap-3">
                    {['true', 'false'].map(v => (
                      <button key={v} onClick={() => setAnswers({...answers, [q.id]: v})}
                        className={`flex-1 p-3 rounded-xl border-2 font-bold text-sm capitalize ${answers[q.id] === v ? 'border-[#1B4F8A] bg-[#1B4F8A]/10' : 'border-[#E5DACE]'}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                )}

                {/* Fill blank / Integer / Passage / Diagram / Ordering */}
                {['fill_blank', 'integer_type', 'passage_based', 'diagram_based', 'ordering'].includes(q.question_type) && (
                  <input data-testid="assign-answer-input" value={answers[q.id] || ''} onChange={e => setAnswers({...answers, [q.id]: e.target.value})}
                    placeholder="Type your answer" className="w-full rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none" />
                )}

                {/* Match */}
                {(q.question_type === 'match' || q.question_type === 'matrix_match') && (
                  <div>
                    {q.match_pairs && (() => { try { return JSON.parse(q.match_pairs); } catch { return []; } })().map((p, i) => (
                      <div key={i} className="flex gap-2 text-sm mb-1"><span className="font-bold">{i+1}. {p.left}</span> <span className="text-slate-400">→</span> <span>{p.right}</span></div>
                    ))}
                    <input data-testid="assign-match-input" value={answers[q.id] || ''} onChange={e => setAnswers({...answers, [q.id]: e.target.value})}
                      placeholder="e.g. 1-A,2-B,3-C,4-D" className="w-full mt-2 rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none" />
                  </div>
                )}

                {/* Subjective - with steps */}
                {q.question_type === 'subjective' && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 block">Final Answer</label>
                      <input data-testid="assign-subjective-answer" value={answers[q.id] || ''} onChange={e => setAnswers({...answers, [q.id]: e.target.value})}
                        placeholder="Your final answer" className="w-full rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-500 mb-1 block">Show your working (step by step)</label>
                      <textarea data-testid="assign-subjective-steps" rows={4} value={steps[q.id] || ''}
                        onChange={e => setSteps({...steps, [q.id]: e.target.value})}
                        placeholder="Step 1: ...\nStep 2: ...\nStep 3: ..."
                        className="w-full rounded-xl border-2 border-[#E5DACE] px-4 py-3 text-sm font-bold focus:border-[#1B4F8A] outline-none resize-y" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="flex gap-2">
              {currentIdx > 0 && (
                <Button onClick={() => setCurrentIdx(i => i - 1)} variant="outline" className="flex-1 font-bold rounded-full border-2 border-[#E5DACE]">Previous</Button>
              )}
              {currentIdx < questions.length - 1 ? (
                <Button data-testid="assign-next-btn" onClick={() => setCurrentIdx(i => i + 1)} className="flex-1 bg-[#1B4F8A] text-white font-bold rounded-full">Next</Button>
              ) : (
                <Button data-testid="assign-submit-btn" onClick={submitAll} className="flex-1 bg-[#FF6B35] text-white font-extrabold rounded-full shadow-[0_4px_0_#C04918]">Submit All</Button>
              )}
            </div>
          </>
        )}

        {/* Results */}
        {submitted && result && (
          <Card className="border-2 border-[#06D6A0] rounded-2xl">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-12 h-12 text-[#06D6A0] mx-auto mb-3" />
              <p className="text-2xl font-black text-[#1B4F8A] mb-1">{result.score_percent}%</p>
              <p className="text-sm font-bold text-slate-500">{result.correct}/{result.total} correct</p>
              <p className="text-sm font-bold text-[#FFD23F] mt-2">+{result.coins_earned} MathCoins earned!</p>
              <Button data-testid="back-to-assignments-btn" onClick={() => { setActiveAssignment(null); fetchAssignments(); }} className="mt-4 bg-[#1B4F8A] text-white font-bold rounded-full px-6">
                Back to Assignments
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
